import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { isServer } from './isServer'
import { getPageContent } from './api'
import { serverData } from './serverData'
import { formatArticleDate } from './InsightsIndex'

// Single market-insights article.
// The server injects the matched article object into the #insightsdata
// island. On client-side navigation the component falls back to fetching
// the `insights` contents row and finding the published article by slug.

const readIslandArticle = () => {
  if (isServer) return undefined // server uses serverData (see initial state)
  const islandEl = document.getElementById('insightsdata')
  if (islandEl && islandEl.innerHTML.trim()) {
    try {
      const parsed = JSON.parse(islandEl.innerHTML)
      if (parsed === null) return null // server resolved this slug as not-found
      // Island holds the single article object on this route
      if (parsed && !Array.isArray(parsed)) return parsed
    } catch (e) {
      // fall through to API fetch
    }
  }
  return undefined // resolving (client-side nav) — fetch from API
}

const InsightPage = () => {
  const { slug } = useParams()
  // Server: read the article the route stashed (null = not found). Client:
  // read the data island, else fetch on client-side nav.
  const [article, setArticle] = useState(() =>
    isServer ? (serverData.insightArticle !== undefined ? serverData.insightArticle : null) : readIslandArticle()
  )

  // Client-side nav fallback: fetch the contents row and find the article
  useEffect(() => {
    if (article === undefined) {
      getPageContent('insights', (data) => {
        let found = null
        if (data && data.length && data[0].value) {
          try {
            const all = JSON.parse(data[0].value)
            found = all.find(a => a.slug === slug && a.published) || null
          } catch (e) {
            found = null
          }
        }
        setArticle(found)
      }, () => setArticle(null))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  if (article === undefined) {
    // Resolving on the client — render nothing to avoid a flash of "not found"
    return <div className="main-body"><div className="column" /></div>
  }

  if (!article) {
    return (
      <div className="main-body">
        <div className="column">
          <h1 className="main-page-header">Article not found</h1>
          <p>
            We couldn't find that article.{' '}
            <Link to="/insights">Browse all market insights</Link>.
          </p>
        </div>
      </div>
    )
  }

  const url = `https://www.kellands.co.nz/insights/${article.slug}`
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    dateModified: article.updated || article.date,
    author: { '@type': 'Organization', name: article.author || 'Kellands Real Estate' },
    publisher: {
      '@type': 'Organization',
      name: 'Kellands Real Estate',
      url: 'https://www.kellands.co.nz',
    },
    mainEntityOfPage: url,
  }

  return (
    <div className="main-body">
      <Helmet>
        <meta charSet="utf-8" />
        <title>{`${article.title} | Kellands Real Estate`}</title>
        <meta name="description" content={article.excerpt} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={url} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <div className="column suburb-page insight-article">
        <h1 className="main-page-header">{article.title}</h1>
        <p className="insight-meta">
          {formatArticleDate(article.date)}
          {article.author ? ` · ${article.author}` : ''}
        </p>
        <div
          className="insight-body"
          dangerouslySetInnerHTML={{ __html: article.body || '' }}
        />

        <section className="suburb-section suburb-cta">
          <p>
            <Link to="/insights">← Back to all market insights</Link>
          </p>
        </section>
      </div>
    </div>
  )
}

export default InsightPage
